import type { LiveReplayFold, LiveReplayLine, LiveReplayPerson, LiveReplaySlot } from '@/api/internal'

export type Shown = {
  assignments: Record<string, LiveReplaySlot>
  people: Record<string, LiveReplayPerson>
  lines: Record<string, string>
}

export const EMPTY_SHOWN: Shown = { assignments: {}, people: {}, lines: {} }

const CHECKPOINT_EVERY = 64

const patch = <V>(base: Record<string, V>, set: Record<string, V>, removed: string[]): Record<string, V> => {
  if (Object.keys(set).length === 0 && removed.length === 0) return base
  const next = { ...base, ...set }
  for (const key of removed) delete next[key]
  return next
}

export const applyFold = (shown: Shown, fold: LiveReplayFold): Shown => {
  const assignments = patch(shown.assignments, fold.assignments, fold.removed_assignments)
  const people = patch(shown.people, fold.people, fold.removed_people)
  const lines = patch(shown.lines, fold.lines, fold.removed_lines)
  if (assignments === shown.assignments && people === shown.people && lines === shown.lines) return shown
  return { assignments, people, lines }
}

// Snapshotting every fold copies the whole map per fold; checkpoints bound both memory and a scrub's replay cost.
export const buildCheckpoints = (folds: LiveReplayFold[]): Shown[] => {
  const checkpoints: Shown[] = []
  let shown = EMPTY_SHOWN
  folds.forEach((fold, i) => {
    shown = applyFold(shown, fold)
    if (i % CHECKPOINT_EVERY === 0) checkpoints.push(shown)
  })
  return checkpoints
}

export const shownAt = (folds: LiveReplayFold[], checkpoints: Shown[], index: number): Shown => {
  if (index < 0) return EMPTY_SHOWN
  const base = Math.floor(index / CHECKPOINT_EVERY)
  let shown = checkpoints[base]
  for (let i = base * CHECKPOINT_EVERY + 1; i <= index; i++) shown = applyFold(shown, folds[i])
  return shown
}

const personFor = (line: LiveReplayLine, shown: Shown): string | null => {
  const pinned = shown.lines[line.entry_id]
  if (line.source === 'system' && pinned !== undefined && shown.people[pinned]) return pinned
  const slot = shown.assignments[String(line.speaker_id)]
  const personId = slot ? (slot.dom ?? slot.mic) : null
  return personId && shown.people[personId] ? personId : null
}

export const labelFor = (line: LiveReplayLine, shown: Shown): string => {
  const personId = personFor(line, shown)
  if (personId !== null) return shown.people[personId].name
  return line.source === 'mic' ? 'You' : 'Others'
}

export type ReplayTurn = { key: string; label: string; lines: LiveReplayLine[] }

export const groupTurns = (lines: LiveReplayLine[], shown: Shown): ReplayTurn[] => {
  const turns: ReplayTurn[] = []
  let previousIdentity: string | null = null
  for (const line of lines) {
    const personId = personFor(line, shown)
    const identity = JSON.stringify([line.segment, line.source, personId, personId === null ? line.speaker_id : null])
    if (identity === previousIdentity) {
      turns[turns.length - 1].lines.push(line)
    } else {
      turns.push({ key: line.entry_id, label: labelFor(line, shown), lines: [line] })
      previousIdentity = identity
    }
  }
  return turns
}

export const visibleLines = (lines: LiveReplayLine[], tMs: number): LiveReplayLine[] => {
  let end = 0
  while (end < lines.length && lines[end].appear_ms <= tMs) end++
  return lines.slice(0, end)
}

export const formatMs = (ms: number): string => {
  const total = Math.max(0, Math.floor(ms / 1000))
  const minutes = Math.floor(total / 60)
  const seconds = total % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

export type Reassignment = { from: string; to: string; lines: LiveReplayLine[] }
export type ReassignmentFold = { index: number; atMs: number; changes: Reassignment[] }

// Speaker keys and pinned entries whose label this fold can change, so a fold rechecks only the lines it touched.
const touchedBy = (fold: LiveReplayFold, next: Shown) => {
  const speakers = new Set([...Object.keys(fold.assignments), ...fold.removed_assignments])
  const entries = new Set([...Object.keys(fold.lines), ...fold.removed_lines])
  const people = new Set([...Object.keys(fold.people), ...fold.removed_people])
  if (people.size > 0) {
    for (const [key, slot] of Object.entries(next.assignments)) {
      const personId = slot.dom ?? slot.mic
      if (personId && people.has(personId)) speakers.add(key)
    }
    for (const [entryId, personId] of Object.entries(next.lines)) if (people.has(personId)) entries.add(entryId)
  }
  return { speakers, entries }
}

// Walks every fold once, keeping each shown line's label, so the log costs one pass instead of a scrub per fold.
export const buildReassignmentLog = (folds: LiveReplayFold[], lines: LiveReplayLine[]): ReassignmentFold[] => {
  const log: ReassignmentFold[] = []
  const labels = new Map<string, string>()
  const shownBySpeaker = new Map<string, number[]>()
  const shownByEntry = new Map<string, number>()
  let shown = EMPTY_SHOWN
  let shownCount = 0
  folds.forEach((fold, index) => {
    const next = applyFold(shown, fold)
    if (next !== shown && shownCount > 0) {
      const { speakers, entries } = touchedBy(fold, next)
      const touched = new Set<number>()
      speakers.forEach((key) => shownBySpeaker.get(key)?.forEach((i) => touched.add(i)))
      entries.forEach((entryId) => {
        const i = shownByEntry.get(entryId)
        if (i !== undefined) touched.add(i)
      })
      const changes = new Map<string, Reassignment>()
      for (const i of Array.from(touched).sort((a, b) => a - b)) {
        const line = lines[i]
        const before = labels.get(line.entry_id)
        const after = labelFor(line, next)
        if (before === undefined || before === after) continue
        const key = JSON.stringify([before, after])
        const change = changes.get(key) ?? { from: before, to: after, lines: [] }
        change.lines.push(line)
        changes.set(key, change)
        labels.set(line.entry_id, after)
      }
      if (changes.size > 0) log.push({ index, atMs: fold.at_ms, changes: Array.from(changes.values()) })
    }
    shown = next
    while (shownCount < lines.length && lines[shownCount].appear_ms <= fold.at_ms) {
      const line = lines[shownCount]
      labels.set(line.entry_id, labelFor(line, shown))
      const speaker = String(line.speaker_id)
      const indexes = shownBySpeaker.get(speaker) ?? []
      indexes.push(shownCount)
      shownBySpeaker.set(speaker, indexes)
      shownByEntry.set(line.entry_id, shownCount)
      shownCount++
    }
  })
  return log
}
